import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, Loader2, XCircle } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import MainLayout from "../components/MainLayout";
import { Button } from "../components/ui/button";

const PremiumSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { getAuthHeaders, API, updateUser } = useAuth();
  const [status, setStatus] = useState("loading"); // loading, success, error
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 5;

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else {
      setStatus("error");
    }
  }, []);

  const pollPaymentStatus = async (sessionId) => {
    if (attempts >= maxAttempts) {
      setStatus("error");
      return;
    }

    try {
      const response = await axios.get(
        `${API}/checkout/status/${sessionId}`,
        { headers: getAuthHeaders() }
      );

      if (response.data.payment_status === "paid") {
        setStatus("success");
        updateUser({ is_premium: true });
      } else if (response.data.status === "expired") {
        setStatus("error");
      } else {
        // Keep polling
        setAttempts(prev => prev + 1);
        setTimeout(() => pollPaymentStatus(sessionId), 2000);
      }
    } catch (error) {
      console.error("Error checking payment:", error);
      setAttempts(prev => prev + 1);
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(sessionId), 2000);
      } else {
        setStatus("error");
      }
    }
  };

  return (
    <MainLayout>
      <div className="max-w-lg mx-auto p-6 flex items-center justify-center min-h-[60vh]" data-testid="premium-success-page">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-card border border-border/50 rounded-3xl p-8 text-center w-full"
        >
          {status === "loading" && (
            <>
              <div className="w-16 h-16 bg-secondary rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Loader2 className="w-8 h-8 text-primary animate-spin" />
              </div>
              <h1 className="font-heading text-2xl font-semibold mb-2">
                Verificando pago...
              </h1>
              <p className="text-muted-foreground">
                Por favor espera mientras confirmamos tu suscripción.
              </p>
            </>
          )}

          {status === "success" && (
            <>
              <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-primary" />
              </div>
              <h1 className="font-heading text-2xl font-semibold mb-2">
                ¡Bienvenido a Premium!
              </h1>
              <p className="text-muted-foreground mb-6">
                Tu suscripción ha sido activada correctamente. 
                Ya puedes disfrutar de todas las funciones avanzadas.
              </p>
              <div className="flex flex-col gap-3">
                <Button 
                  onClick={() => navigate("/chat")}
                  className="rounded-full"
                  data-testid="go-to-chat-btn"
                >
                  Ir al Chat
                </Button>
                <Button 
                  variant="ghost"
                  onClick={() => navigate("/dashboard")}
                  className="rounded-full"
                >
                  Ver Dashboard
                </Button>
              </div>
            </>
          )}

          {status === "error" && (
            <>
              <div className="w-16 h-16 bg-destructive/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-8 h-8 text-destructive" />
              </div>
              <h1 className="font-heading text-2xl font-semibold mb-2">
                Error en el pago
              </h1>
              <p className="text-muted-foreground mb-6">
                No pudimos verificar tu pago. Si crees que es un error, 
                contacta con soporte.
              </p>
              <div className="flex flex-col gap-3">
                <Button 
                  onClick={() => navigate("/premium")}
                  className="rounded-full"
                >
                  Intentar de nuevo
                </Button>
                <Button 
                  variant="ghost"
                  onClick={() => navigate("/chat")}
                  className="rounded-full"
                >
                  Volver al Chat
                </Button>
              </div>
            </>
          )}
        </motion.div>
      </div>
    </MainLayout>
  );
};

export default PremiumSuccessPage;
